import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { AlertCircle, Plus } from "lucide-react";

/**
 * Dialog dedicat pentru adăugarea PSA-urilor cu numerotare prefixată:
 *   - Mască, Furtun, Curea — model OBLIGATORIU (3M, Sundström, sau custom).
 *   - 3M → M1, M2, M3... | Sundström → S1, S2... | custom → user alege prefix 1-2 litere.
 *   - Numerotare globală pe firmă per (categorie + model).
 *
 * Pentru intrări retroactive (cumpărate înainte de iunie) → toggle "Fără prefix"
 * permite tastarea manuală a primului număr (ex: 27 → 27, 28, 29...).
 */

const PREFIXED_CATEGORIES = [
  { code: "masca", label: "Mască", icon: "😷" },
  { code: "furtun", label: "Furtun", icon: "🧯" },
  { code: "curea", label: "Curea", icon: "🪢" },
];

const COMMON_SIZES = {
  masca: ["S", "M", "L", "XL", "Unica"],
  furtun: ["Unica", "Scurt", "Lung"],
  curea: ["S", "M", "L", "XL", "Unica"],
};

const LOCATIONS_FALLBACK = ["MAGAZIE", "PAXLI", "ACASA VICTOR"];

export default function PSABatchDialog({ defaultCategory, meta, onClose, onSaved }) {
  const [category, setCategory] = useState(defaultCategory || "masca");
  const [models, setModels] = useState([]); // [{ category, model, prefix, last_number }]
  const [model, setModel] = useState("");
  const [size, setSize] = useState("");
  const [count, setCount] = useState(1);
  const [location, setLocation] = useState("MAGAZIE");
  const [supplier, setSupplier] = useState("");
  const [price, setPrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [note, setNote] = useState("");
  const [noPrefix, setNoPrefix] = useState(false);
  const [startingSerial, setStartingSerial] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addModelOpen, setAddModelOpen] = useState(false);

  const locOptions = meta?.locations?.length ? meta.locations : LOCATIONS_FALLBACK;
  const sizeSuggestions = COMMON_SIZES[category] || [];

  const loadModels = async () => {
    try {
      const r = await api.get("/inventory/psa-model-registry", { params: { category } });
      setModels(r.data || []);
      // Auto-select first model if none selected
      const list = r.data || [];
      if (list.length > 0 && !list.find((m) => m.model === model)) {
        setModel(list[0].model);
      }
    } catch {
      setModels([]);
    }
  };
  useEffect(() => { loadModels(); /* eslint-disable-next-line */ }, [category]);

  // Live preview when not in retroactive mode
  useEffect(() => {
    if (noPrefix || !model || !category || count < 1) { setPreview(null); return; }
    let cancelled = false;
    api.get("/inventory/psa-model-registry/preview", { params: { category, model, count } })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [category, model, count, noPrefix]);

  // Retroactive preview
  const retroactivePreview = useMemo(() => {
    if (!noPrefix || !startingSerial || count < 1) return null;
    const m = String(startingSerial).match(/^(.*?)(\d+)$/);
    if (!m) return count === 1 ? [startingSerial] : null;
    const head = m[1] ?? "";
    const start = parseInt(m[2], 10);
    const width = m[2].length;
    return Array.from({ length: count }, (_, i) => `${head}${String(start + i).padStart(width, "0")}`);
  }, [noPrefix, startingSerial, count]);

  const submit = async () => {
    if (!category) { toast.error("Selectează categoria"); return; }
    if (!model && !noPrefix) { toast.error("Modelul este obligatoriu"); return; }
    if (!size.trim()) { toast.error("Mărimea este obligatorie"); return; }
    if (count < 1) { toast.error("Cantitatea trebuie cel puțin 1"); return; }
    if (noPrefix && !startingSerial.trim()) { toast.error("Pentru retroactiv: tastează primul număr de serie"); return; }

    setSaving(true);
    try {
      const payload = {
        psa_category: category,
        model: noPrefix ? (model || "—") : model,
        psa_size: size.trim(),
        count: parseInt(count) || 1,
        location: location || "MAGAZIE",
        supplier: supplier.trim() || null,
        price_chf: price === "" ? null : parseFloat(price),
        purchase_date: purchaseDate || null,
        note: note.trim() || null,
        no_prefix: noPrefix,
        starting_serial: noPrefix ? startingSerial.trim() : null,
      };
      const r = await api.post("/inventory/psa-batch", payload);
      const serials = r.data?.serials || [];
      toast.success(`${serials.length} ${serials.length === 1 ? "PSA adăugat" : "PSA-uri adăugate"}: ${serials.join(", ")}`);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la adăugare");
    } finally { setSaving(false); }
  };

  const previewLabels = noPrefix ? retroactivePreview : (preview?.labels || null);

  return (
    <>
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="psa-batch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            🦺 Adaugă PSA — numerotare automată
          </DialogTitle>
          <p className="text-[12px] text-zinc-500">
            Pentru mască · furtun · curea: modelul este obligatoriu (3M, Sundström, sau alt model salvat).
            Sistemul atribuie automat numere consecutive: <strong className="font-mono">M1, M2…</strong> pentru 3M, <strong className="font-mono">S1, S2…</strong> pentru Sundström.
          </p>
        </DialogHeader>

        <div className="space-y-4">
          {/* 1. Categorie */}
          <div>
            <Label className="small-label">1. Categorie PSA *</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {PREFIXED_CATEGORIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => setCategory(c.code)}
                  className={`text-sm font-bold px-3 py-3 rounded border-2 transition-all ${category === c.code ? "bg-[var(--primary)] text-white border-[var(--primary)] scale-[1.02]" : "bg-white border-zinc-300 hover:border-zinc-400"}`}
                  data-testid={`psa-batch-cat-${c.code}`}
                >
                  <div className="text-2xl mb-1">{c.icon}</div>
                  <div>{c.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Toggle: cu prefix / fără prefix */}
          <div className="card-flat p-2 bg-zinc-50 flex items-center gap-3 text-sm">
            <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="psa-batch-no-prefix-toggle">
              <input type="checkbox" checked={noPrefix} onChange={(e) => setNoPrefix(e.target.checked)} className="rounded"/>
              <span className="font-semibold">Retroactiv (fără prefix M/S)</span>
            </label>
            <span className="text-[11px] text-zinc-500">
              Bifează pentru PSA cumpărate înainte de iunie 2026 — folosește numerotarea manuală existentă.
            </span>
          </div>

          {/* 2. Model */}
          {!noPrefix && (
            <div>
              <div className="flex items-center justify-between">
                <Label className="small-label">2. Model *</Label>
                <button
                  type="button"
                  onClick={() => setAddModelOpen(true)}
                  className="text-[11px] font-bold text-blue-700 hover:underline"
                  data-testid="psa-batch-add-model-btn"
                >
                  <Plus size={11} className="inline mr-0.5"/> Adaugă model nou
                </button>
              </div>
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger className="mt-1" data-testid="psa-batch-model"><SelectValue placeholder="Selectează model" /></SelectTrigger>
                <SelectContent>
                  {models.length === 0 && <div className="px-3 py-2 text-xs text-zinc-500 italic">Niciun model — adaugă unul.</div>}
                  {models.map((m) => (
                    <SelectItem key={m.id || `${m.model}-${m.prefix}`} value={m.model}>
                      <span className="font-mono font-bold mr-1.5">{m.prefix}</span> {m.model} <span className="text-[10px] text-zinc-500 ml-1">· ultim #{m.last_number}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* 3. Mărime — obligatoriu */}
          <div>
            <Label className="small-label">3. Mărime *</Label>
            <div className="mt-1 flex gap-2 items-center">
              <Input
                value={size}
                onChange={(e) => setSize(e.target.value)}
                placeholder="ex: M, L, 42, Unica"
                className="flex-1"
                data-testid="psa-batch-size"
              />
            </div>
            {sizeSuggestions.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {sizeSuggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSize(s)}
                    className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${size === s ? "bg-zinc-800 text-white border-zinc-800" : "bg-white border-zinc-300 hover:bg-zinc-50"}`}
                    data-testid={`psa-batch-size-quick-${s}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 4. Cantitate */}
          <div>
            <Label className="small-label">4. Câte bucăți? *</Label>
            <Input
              type="number"
              min={1}
              max={200}
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value) || 1)}
              className="mt-1 max-w-[160px]"
              data-testid="psa-batch-count"
            />
          </div>

          {/* 4b. Starting serial pt retroactiv */}
          {noPrefix && (
            <div className="card-flat p-3 bg-amber-50/40 border-l-4 border-l-amber-400">
              <Label className="small-label">Primul număr de serie (retroactiv) *</Label>
              <Input
                value={startingSerial}
                onChange={(e) => setStartingSerial(e.target.value)}
                placeholder="ex: 27, 100, A-27..."
                className="mt-1"
                data-testid="psa-batch-starting-serial"
              />
              <p className="text-[11px] text-zinc-600 mt-1.5 italic">
                Următoarele se vor incrementa automat (ex: 27 → 27, 28, 29).
              </p>
            </div>
          )}

          {/* PREVIEW */}
          {previewLabels && previewLabels.length > 0 && (
            <div className="card-flat p-3 bg-emerald-50 border-l-4 border-l-emerald-500" data-testid="psa-batch-preview">
              <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-800 mb-1.5">
                Se vor crea {previewLabels.length} {previewLabels.length === 1 ? "bucată" : "bucăți"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {previewLabels.map((s) => (
                  <span key={s} className="font-mono text-xs font-bold bg-white border-2 border-emerald-300 text-emerald-900 px-2 py-0.5 rounded">
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* 5. Optional fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="small-label">Locație</Label>
              <Select value={location} onValueChange={setLocation}>
                <SelectTrigger className="mt-1" data-testid="psa-batch-location"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {locOptions.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="small-label">Furnizor (opțional)</Label>
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="ex: 3M Switzerland" className="mt-1" data-testid="psa-batch-supplier"/>
            </div>
            <div>
              <Label className="small-label">Preț unitar CHF (opțional)</Label>
              <Input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="ex: 145.50" className="mt-1" data-testid="psa-batch-price"/>
            </div>
            <div>
              <Label className="small-label">Data cumpărării (opțional)</Label>
              <Input type="date" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="mt-1" data-testid="psa-batch-purchase-date"/>
            </div>
            <div className="sm:col-span-2">
              <Label className="small-label">Notă (opțional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: primite pe 01.02.2026 de la furnizor" className="mt-1" data-testid="psa-batch-note"/>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="psa-batch-save">
            {saving ? "Se salvează…" : `Adaugă ${count} buc.`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {addModelOpen && (
      <AddModelDialog
        category={category}
        onClose={() => setAddModelOpen(false)}
        onSaved={(newModel) => {
          setModel(newModel);
          loadModels();
          setAddModelOpen(false);
        }}
      />
    )}
    </>
  );
}

function AddModelDialog({ category, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [prefix, setPrefix] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Numele modelului obligatoriu"); return; }
    const p = prefix.trim().toUpperCase();
    if (!/^[A-Z]{1,2}$/.test(p)) { toast.error("Prefix-ul trebuie 1-2 litere (A-Z)"); return; }
    setSaving(true);
    try {
      await api.post("/inventory/psa-model-registry", {
        category, model: name.trim(), prefix: p,
      });
      toast.success(`Model „${name.trim()}" salvat cu prefix „${p}"`);
      onSaved(name.trim());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="add-model-dialog">
        <DialogHeader>
          <DialogTitle>Model nou pentru categoria: {category}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2 items-start">
            <AlertCircle size={16} className="text-blue-600 mt-1 shrink-0"/>
            <p className="text-[12px] text-zinc-600">
              Modelele se salvează în registru și vor fi disponibile pentru toate viitoarele adăugări. Prefix-ul (1-2 litere) determină numerotarea: ex. <strong className="font-mono">DR1, DR2…</strong> pentru Dräger.
            </p>
          </div>
          <div>
            <Label className="small-label">Numele modelului *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex: Dräger, MSA, Honeywell..." className="mt-1" data-testid="new-model-name" autoFocus/>
          </div>
          <div>
            <Label className="small-label">Prefix (1-2 litere mari) *</Label>
            <Input
              value={prefix}
              onChange={(e) => setPrefix(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="ex: D, DR, HW"
              className="mt-1 font-mono uppercase max-w-[120px]"
              maxLength={2}
              data-testid="new-model-prefix"
            />
            {prefix && /^[A-Z]{1,2}$/.test(prefix) && (
              <p className="text-[11px] text-emerald-700 mt-1">
                Numerele vor arăta ca: <span className="font-mono font-bold">{prefix}1, {prefix}2, {prefix}3</span>...
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} className="bg-[var(--primary)]" data-testid="new-model-save">Salvează</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
